
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Job } from '@/components/jobs/types';
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  TrendingUp, 
  Clock, 
  Target, 
  Eye, 
  Send, 
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";

interface AnalyticsTabProps {
  job: Job;
}

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ job }) => {
  // Mock analytics data - in production this would come from your analytics service
  const analytics = {
    views: 2847,
    applications: 156,
    conversionRate: 5.5,
    avgTimeToApply: "3.2 days",
    sourceBreakdown: [
      { source: "LinkedIn", applications: 68, percentage: 44 },
      { source: "Company Website", applications: 41, percentage: 26 },
      { source: "Indeed", applications: 32, percentage: 21 },
      { source: "Referrals", applications: 15, percentage: 9 }
    ],
    stageMetrics: [
      { stage: "Applied", count: 156, percentage: 100 },
      { stage: "Screening", count: 89, percentage: 57 },
      { stage: "Interview", count: 34, percentage: 22 },
      { stage: "Final Round", count: 12, percentage: 8 },
      { stage: "Offer", count: 3, percentage: 2 }
    ],
    timeMetrics: {
      avgTimeToScreen: "2.1 days",
      avgTimeToInterview: "5.8 days",
      avgTimeToOffer: "12.3 days",
      totalTimeToHire: "18.7 days"
    },
    demographics: {
      experienceLevel: [
        { level: "Junior (0-2 years)", count: 47, percentage: 30 },
        { level: "Mid (3-5 years)", count: 78, percentage: 50 },
        { level: "Senior (6+ years)", count: 31, percentage: 20 }
      ],
      location: [
        { location: "Local", count: 94, percentage: 60 },
        { location: "Remote", count: 62, percentage: 40 }
      ]
    }
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Eye className="h-4 w-4 text-blue-600" />
              Total Views
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.views.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 inline mr-1" />
              +12% vs last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Send className="h-4 w-4 text-green-600" />
              Applications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.applications}</div>
            <p className="text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 inline mr-1" />
              +8% vs last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-purple-600" />
              Conversion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.conversionRate}%</div>
            <p className="text-xs text-muted-foreground">
              Views to applications
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              Avg. Time to Apply
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.avgTimeToApply}</div>
            <p className="text-xs text-muted-foreground">
              From first view to application
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Application Sources */}
      <Card>
        <CardHeader>
          <CardTitle>Application Sources</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics.sourceBreakdown.map((source, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="font-medium">{source.source}</div>
                  <Badge variant="outline">{source.applications} applications</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={source.percentage} className="w-20 h-2" />
                  <span className="text-sm font-medium w-10">{source.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Hiring Funnel */}
      <Card>
        <CardHeader>
          <CardTitle>Hiring Funnel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics.stageMetrics.map((stage, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {stage.stage === "Applied" && <Send className="h-4 w-4 text-blue-600" />}
                    {stage.stage === "Screening" && <Eye className="h-4 w-4 text-purple-600" />}
                    {stage.stage === "Interview" && <Calendar className="h-4 w-4 text-orange-600" />}
                    {stage.stage === "Final Round" && <Users className="h-4 w-4 text-yellow-600" />}
                    {stage.stage === "Offer" && <CheckCircle className="h-4 w-4 text-green-600" />}
                    <span className="font-medium">{stage.stage}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-lg font-bold">{stage.count}</span>
                  <Badge variant={stage.percentage > 50 ? "default" : "secondary"}>
                    {stage.percentage}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Time to Hire Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Time to Hire Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Avg. Time to Screen</span>
                <span className="text-sm">{analytics.timeMetrics.avgTimeToScreen}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Avg. Time to Interview</span>
                <span className="text-sm">{analytics.timeMetrics.avgTimeToInterview}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Avg. Time to Offer</span>
                <span className="text-sm">{analytics.timeMetrics.avgTimeToOffer}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Total Time to Hire</span>
                <span className="text-sm font-bold">{analytics.timeMetrics.totalTimeToHire}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Candidate Demographics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Experience Level</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.demographics.experienceLevel.map((level, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm">{level.level}</span>
                  <div className="flex items-center gap-2">
                    <Progress value={level.percentage} className="w-16 h-2" />
                    <span className="text-sm font-medium w-8">{level.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Location Preference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.demographics.location.map((loc, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm">{loc.location}</span>
                  <div className="flex items-center gap-2">
                    <Progress value={loc.percentage} className="w-16 h-2" />
                    <span className="text-sm font-medium w-8">{loc.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Performance Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-800">Strong Conversion Rate</p>
                <p className="text-sm text-green-700">Your 5.5% conversion rate is 23% above industry average</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Opportunity: Interview Stage</p>
                <p className="text-sm text-yellow-700">Only 22% of screened candidates reach interview stage. Consider optimizing screening criteria.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Recommendation</p>
                <p className="text-sm text-blue-700">Consider posting to additional job boards to increase visibility and applications.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
